import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";

// Pages
import HomePage from "./pages/HomePage";
import LoginPage, { RegisterPage } from "./pages/AuthPages";
import ExplorePage from "./pages/ExplorePage";
import ListingDetailPage from "./pages/ListingDetailPage";
import CategoriesPage from "./pages/CategoriesPage";
import FavoritesPage from "./pages/FavoritesPage";
import PricingPage from "./pages/PricingPage";
import DashboardLayout, { DashboardHome } from "./pages/DashboardPage";
import MyListingsPage from "./pages/MyListingsPage";
import CreateListingPage from "./pages/CreateListingPage";
import MessagesPage from "./pages/MessagesPage";
import AIReceptionistPage from "./pages/AIReceptionistPage";
import AuctionsPage from "./pages/AuctionsPage";
import AuctionDetailPage from "./pages/AuctionDetailPage";
import CreateAuctionPage from "./pages/CreateAuctionPage";
import ForgotPasswordPage, { ResetPasswordPage } from "./pages/ForgotPasswordPage";
import ProtectedPaymentsPage from "./pages/ProtectedPaymentsPage";
import ProtectedPurchaseStatusPage from "./pages/ProtectedPurchaseStatusPage";
import SettingsPage from "./pages/SettingsPage";
import AdminMorphReviewPage from "./pages/AdminMorphReviewPage";
import EnbixosOperatorPage from "./pages/EnbixosOperatorPage";
import CreateClutchPage from "./pages/CreateClutchPage";
import ClutchDetailPage from "./pages/ClutchDetailPage";
import MyClutchesPage from "./pages/MyClutchesPage";
import PublicClutchPage from "./pages/PublicClutchPage";

// Admin Pages
import AdminLayout from "./pages/AdminLayout";
import AdminDashboard from "./pages/AdminDashboard";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminModerationPage from "./pages/AdminModerationPage";
import AdminSubscriptionsPage from "./pages/AdminSubscriptionsPage";
import AdminMessagesPage from "./pages/AdminMessagesPage";
import AdminSupportPage from "./pages/AdminSupportPage";
import AdminAuctionsPage from "./pages/AdminAuctionsPage";
import AdminEnbixosPage from "./pages/AdminEnbixosPage";
import AdminCategoriesPage from "./pages/AdminCategoriesPage";
import AdminCompliancePage from "./pages/AdminCompliancePage";
import SupportPage from "./pages/SupportPage";

// Legal Pages
import TerminosPage from "./pages/legal/TerminosPage";
import PrivacidadPage from "./pages/legal/PrivacidadPage";
import PoliticasPage from "./pages/legal/PoliticasPage";

// Protected Route Component
function ProtectedRoute({ children, requireSeller = false }) {
  const { user, loading, isSeller, isSuperAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Super admin should go to admin panel UNLESS impersonating
  const isImpersonating = !!localStorage.getItem('impersonating');
  if (isSuperAdmin && !isImpersonating && window.location.pathname.startsWith('/dashboard')) {
    return <Navigate to="/admin" replace />;
  }
  
  if (requireSeller && !isSeller) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

function AdminRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#060608] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user || !isAdmin) return <Navigate to="/login" replace />;
  return children;
}

// Layout with Navbar and Footer
function ImpersonationBanner() {
  const impersonating = localStorage.getItem('impersonating');
  if (!impersonating) return null;
  const data = JSON.parse(impersonating);
  const exitViewAs = () => {
    const adminToken = localStorage.getItem('admin_original_token');
    if (adminToken) {
      localStorage.setItem('access_token', adminToken);
      localStorage.removeItem('admin_original_token');
      localStorage.removeItem('impersonating');
      window.location.href = '/admin/users';
    }
  };
  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-black px-4 py-2 flex items-center justify-center gap-4 text-sm font-medium" data-testid="impersonation-banner">
      <span>Viendo como: {data.name} ({data.email})</span>
      <button onClick={exitViewAs} className="bg-black/20 hover:bg-black/30 px-3 py-1 rounded-full text-xs font-bold transition-colors" data-testid="exit-view-as">
        Volver a Admin
      </button>
    </div>
  );
}

function MainLayout({ children, showFooter = true }) {
  return (
    <>
      <Navbar />
      <main>{children}</main>
      {showFooter && <Footer />}
    </>
  );
}

function AppRoutes() {
  return (
    <>
    <ImpersonationBanner />
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/registro" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/explorar" element={<MainLayout><ExplorePage /></MainLayout>} />
      <Route path="/categorias" element={<MainLayout><CategoriesPage /></MainLayout>} />
      <Route path="/categoria/:slug" element={<MainLayout><ExplorePage /></MainLayout>} />
      <Route path="/anuncio/:id" element={<MainLayout><ListingDetailPage /></MainLayout>} />
      <Route path="/precios" element={<MainLayout><PricingPage /></MainLayout>} />
      <Route path="/subastas" element={<MainLayout><AuctionsPage /></MainLayout>} />
      <Route path="/subastas/:id" element={<MainLayout><AuctionDetailPage /></MainLayout>} />
      <Route path="/compra-protegida/:sessionId" element={<MainLayout><ProtectedPurchaseStatusPage /></MainLayout>} />
      <Route path="/compra-protegida/canceled" element={<MainLayout><ProtectedPurchaseStatusPage /></MainLayout>} />
      <Route path="/terminos" element={<MainLayout><TerminosPage /></MainLayout>} />
      <Route path="/privacidad" element={<MainLayout><PrivacidadPage /></MainLayout>} />
      <Route path="/politicas" element={<MainLayout><PoliticasPage /></MainLayout>} />
      <Route path="/criadores" element={<MainLayout><ExplorePage /></MainLayout>} />
      <Route path="/criador/:id" element={<MainLayout><ExplorePage /></MainLayout>} />
      <Route path="/camadas/:clutchId" element={<MainLayout><PublicClutchPage /></MainLayout>} />

      {/* Protected Routes */}
      <Route path="/favoritos" element={
        <ProtectedRoute>
          <MainLayout><FavoritesPage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/mensajes" element={
        <ProtectedRoute>
          <MainLayout showFooter={false}>
            <div className="pt-20 min-h-screen bg-[#0A0A0C]">
              <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
                <MessagesPage />
              </div>
            </div>
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Dashboard Routes (Any authenticated user) */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <MainLayout showFooter={false}>
            <DashboardLayout />
          </MainLayout>
        </ProtectedRoute>
      }>
        <Route index element={<DashboardHome />} />
        <Route path="anuncios" element={<MyListingsPage />} />
        <Route path="anuncios/nuevo" element={<CreateListingPage />} />
        <Route path="anuncios/:id/editar" element={<CreateListingPage />} />
        <Route path="subastas/nueva" element={<CreateAuctionPage />} />
        <Route path="pagos" element={<ProtectedPaymentsPage />} />
        <Route path="mensajes" element={<MessagesPage />} />
        <Route path="estadisticas" element={<DashboardHome />} />
        <Route path="ia" element={<AIReceptionistPage />} />
        <Route path="suscripcion" element={<PricingPage />} />
        <Route path="configuracion" element={<SettingsPage />} />
        <Route path="admin/morphs" element={<AdminMorphReviewPage />} />
        <Route path="enbixos" element={<EnbixosOperatorPage />} />
        <Route path="camadas" element={<MyClutchesPage />} />
        <Route path="camadas/nueva" element={<CreateClutchPage />} />
        <Route path="camadas/:clutchId" element={<ClutchDetailPage />} />
        <Route path="soporte" element={<SupportPage />} />
      </Route>

      {/* Admin Routes (Separate layout) */}
      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="moderation" element={<AdminModerationPage />} />
        <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
        <Route path="ai" element={<AdminSubscriptionsPage />} />
        <Route path="messages" element={<AdminMessagesPage />} />
        <Route path="support" element={<AdminSupportPage />} />
        <Route path="morphs" element={<AdminMorphReviewPage />} />
        <Route path="auctions" element={<AdminAuctionsPage />} />
        <Route path="enbixos" element={<AdminEnbixosPage />} />
        <Route path="compliance" element={<AdminCompliancePage />} />
        <Route path="categories" element={<AdminCategoriesPage />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster 
            position="top-right" 
            theme="dark"
            toastOptions={{
              style: {
                background: '#121215',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#f4f4f5'
              }
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
